import React, {useMemo, useState} from 'react';
import {Button, FlexColumn, Table, TableBar, Text} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {createFragmentContainer, graphql} from 'react-relay';
import type {SettingsClientGuestUsers_viewer$data as SettingsClientGuestUsers_viewer} from './__generated__/SettingsClientGuestUsers_viewer.graphql';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import Util from '../../shared/util/util';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {hasModule} from '../../shared/util/ModuleUtil';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../../constants';
import CreateProjectPersonClientMutation from '../../../mutations/create_project_person_client_mutation';
import DeleteProjectPersonMutation from '../../../mutations/delete_project_person_mutation';
import DeletePersonMutation from '../../../mutations/delete_person_mutation';
import SettingsClientHeader from './SettingsClientHeader';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import EditGuestUserModalContainer from './EditGuestUserModalContainer';
import type {ClientContactFormValues} from './hooks/useClientContactForm';

const PageWrapper = Styled.div`
	padding: 25px;
`;
const TableBarWrapper = Styled.div`
	margin: 32px 0 24px;
`;
const DescriptionWrapper = Styled(FlexColumn)`
	margin: 8px 0 24px;
`;

type SettingsClientGuestUsersProps = {
	viewer: SettingsClientGuestUsers_viewer;
};

type PersonProjectType = {
	personProjectId: string;
	projectId: string;
	projectName: string;
};

type DisabledClickCheckboxesType = {
	projectId: string;
	personId: string;
};

type EditingGuestUser = {
	personId: string;
	initialValues: ClientContactFormValues;
	projectPersonIdByProject: Map<string, string>;
};

const SettingsClientGuestUsers = ({viewer}: SettingsClientGuestUsersProps) => {
	const history = useHistory();
	const {formatMessage} = useIntl();

	const {client} = viewer.company || {};
	const clientId = client?.id;
	const clientName = client?.name ?? '';
	const [disabledClickCheckboxes, setDisabledClickCheckboxes] = useState<DisabledClickCheckboxesType[]>([]);
	const [editingGuestUser, setEditingGuestUser] = useState<EditingGuestUser | null>(null);

	const personIdToProjectsMap = useMemo(() => {
		const map = new Map<string, PersonProjectType[]>();

		if (viewer.projects?.edges) {
			viewer.projects.edges.forEach(project => {
				const {id: projectId, name: projectName, projectPersons} = project?.node || {};

				if (projectId && projectName && projectPersons) {
					projectPersons.edges?.forEach(pp => {
						const {id: personProjectId, person} = pp?.node || {};

						if (personProjectId && person) {
							const existing = map.get(person.id) || [];
							existing.push({personProjectId, projectId, projectName});
							map.set(person.id, existing);
						}
					});
				}
			});
		}

		return map;
	}, [viewer.projects]);

	const guestUsers = useMemo(() => {
		return (client?.clientUsers?.edges || []).map(edge => {
			const node = edge?.node;
			if (!node) return null;

			const firstName = node.firstName ?? '';
			const lastName = node.lastName ?? '';
			const fullName = firstName ? `${firstName} ${lastName}`.trim() : '';
			const projects = personIdToProjectsMap.get(node.id) ?? [];
			const projectsDisplayLabel = projects.map(p => p.projectName).join(', ');

			return {
				id: node.id,
				firstName,
				lastName,
				email: node.email ?? '',
				phone: node.phone ?? '',
				jobTitle: node.jobTitle ?? '',
				fullName,
				projects,
				projectsDisplayLabel,
			};
		});
	}, [client, personIdToProjectsMap]);

	const onClickAddGuestUser = () => {
		const project_options =
			viewer.projects?.edges
				?.filter(p => p?.node?.client?.id === clientId)
				.map(p => ({
					value: p?.node?.id,
					label: p?.node?.name,
				})) || null;

		showModal({
			type: MODAL_TYPE.ADD_CLIENT_USER,
			addClientUser: true,
			project_options: project_options,
			companyId: viewer.company?.id,
			clientId: clientId,
		});
	};

	const isContactsModuleActive =
		hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENT_CONTACTS_FEATURE) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);

	const onClickEditGuestUser = row => {
		if (isContactsModuleActive) {
			history.push('/admin/team/view-user/' + row.id);
			return;
		}
		const projectPersonIdByProject = new Map<string, string>();
		row.projects.forEach((p: PersonProjectType) => {
			projectPersonIdByProject.set(p.projectId, p.personProjectId);
		});
		setEditingGuestUser({
			personId: row.id,
			initialValues: {
				firstName: row.firstName,
				lastName: row.lastName,
				email: row.email,
				jobTitle: row.jobTitle,
				phone: row.phone,
				projectIds: row.projects.map((p: PersonProjectType) => p.projectId),
			},
			projectPersonIdByProject,
		});
	};

	const onClickDeleteGuestUser = row => {
		const onSuccess = () => {
			createToast({duration: 5000, message: formatMessage({id: 'settings.client-user_deleted'})});
		};

		const callbackPositive = () => {
			Util.CommitMutation(
				DeletePersonMutation,
				{
					id: row.id,
					parentId: clientId,
				},
				onSuccess
			);
		};

		showModal({
			type: MODAL_TYPE.GENERIC_DELETION_WARNING,
			deleteCallback: callbackPositive,
		});
	};

	const projectOptions = useMemo(() => {
		return (
			viewer.projects?.edges?.map(edge => ({
				id: edge?.node?.id,
				name: edge?.node?.name,
			})) || []
		);
	}, [viewer.projects]);

	const onChangeProjectPerson = (projectId: string | undefined, personId: string, projects: PersonProjectType[]) => {
		const onSuccess = (projectId: string, personId: string) => {
			const newDisabledClickCheckboxes = [...disabledClickCheckboxes].filter(
				c => c.personId !== personId && c.projectId !== projectId
			);
			setDisabledClickCheckboxes(newDisabledClickCheckboxes);
		};

		if (!projectId) return;

		setDisabledClickCheckboxes(old => [...old, {projectId, personId}]);

		if (disabledClickCheckboxes.find(c => c.projectId === projectId && c.personId === personId)) return;

		const projectIds = projects.map(p => p.projectId);

		if (projectIds.includes(projectId)) {
			const personProjectId = projects.find(p => p.projectId === projectId)?.personProjectId;

			if (personProjectId) {
				Util.CommitMutation(
					DeleteProjectPersonMutation,
					{
						ids: [personProjectId],
						projectId: projectId,
					},
					() => onSuccess(projectId, personId)
				);
			}
		} else {
			Util.CommitMutation(
				CreateProjectPersonClientMutation,
				{
					personId: personId,
					projectId: projectId,
				},
				() => onSuccess(projectId, personId)
			);
		}
	};

	return (
		<CustomScrollDiv>
			<PageWrapper>
				{clientId ? (
					<SettingsClientHeader
						clientId={clientId}
						clientName={client?.name}
						clientLogoDefaultId={client?.logoDefaultId}
						clientLogoId={client?.logoId}
					/>
				) : null}

				<TableBarWrapper>
					<TableBar title={formatMessage({id: 'settings.client-user'})}>
						<Button emphasis={'low'} onPress={onClickAddGuestUser}>
							<FormattedMessage id="settings_clients.add-client-user" />
						</Button>
					</TableBar>
				</TableBarWrapper>

				<DescriptionWrapper>
					<Text>{formatMessage({id: 'settings.client-contacts-description'})}</Text>
					<Text>{formatMessage({id: 'settings.client-contacts-sub-description'})}</Text>
				</DescriptionWrapper>

				<Table data={guestUsers} width={'100%'}>
					<Table.TextColumn accessorKey={'fullName'} header={formatMessage({id: 'common.name'})} allowSorting />
					<Table.TextColumn accessorKey={'email'} header={formatMessage({id: 'common.email'})} allowSorting />
					<Table.TextColumn accessorKey={'phone'} header={formatMessage({id: 'common.phone'})} allowSorting />

					<Table.DropdownColumn
						header={formatMessage({id: 'common.projects'})}
						label={row => row.projectsDisplayLabel}
						defaultLabel={formatMessage({id: 'settings.client-user-no-assigned-projects'})}
						width={'250px'}
						keepOpenStateOnChange
					>
						{projectOptions.map(p => (
							<Table.DropdownColumn.CheckboxItem
								key={p.id}
								checked={row => row.projects.map(pr => pr.projectId).includes(p.id)}
								onChange={row => onChangeProjectPerson(p.id, row.id, row.projects)}
							>
								<Text size="2">{p.name}</Text>
							</Table.DropdownColumn.CheckboxItem>
						))}
					</Table.DropdownColumn>

					<Table.ActionColumn header={formatMessage({id: 'common.actions'})} hideHeader>
						<Table.ActionColumn.Option onClick={onClickEditGuestUser} data-cy="edit-guest-user">
							<FormattedMessage id={'common.edit'} />
						</Table.ActionColumn.Option>
						<Table.ActionColumn.Option onClick={onClickDeleteGuestUser} data-cy="delete-guest-user">
							<FormattedMessage id={'common.delete'} />
						</Table.ActionColumn.Option>
					</Table.ActionColumn>
				</Table>
			</PageWrapper>
			{editingGuestUser && clientId && (
				<EditGuestUserModalContainer
					personId={editingGuestUser.personId}
					clientId={clientId}
					clientName={clientName}
					initialValues={editingGuestUser.initialValues}
					projectPersonIdByProject={editingGuestUser.projectPersonIdByProject}
					onClose={() => setEditingGuestUser(null)}
				/>
			)}
		</CustomScrollDiv>
	);
};

export const SettingsClientGuestUsersQuery = graphql`
	query SettingsClientGuestUsers_Query($clientId: String) {
		viewer {
			actualPersonId
			component(name: "settings_client")
			...SettingsClientGuestUsers_viewer @arguments(clientId: $clientId)
		}
	}
`;

export default createFragmentContainer(SettingsClientGuestUsers, {
	viewer: graphql`
		fragment SettingsClientGuestUsers_viewer on Viewer @argumentDefinitions(clientId: {type: "String"}) {
			id
			availableFeatureFlags {
				key
			}
			company {
				id
				client(id: $clientId) {
					id
					name
					street
					zip
					city
					vat
					country
					notes
					logoId
					logoDefaultId
					clientUsers(first: 1000) @connection(key: "Client_clientUsers", filters: []) {
						edges {
							node {
								id
								firstName
								lastName
								email
								phone
								jobTitle
							}
						}
					}
				}
			}
			projects(first: 100000) @connection(key: "Viewer_projects", filters: []) {
				edges {
					node {
						id
						name
						client {
							id
						}
						projectPersons(first: 1000, onlyClientUsers: true)
							@connection(key: "Project_projectPersons", filters: []) {
							edges {
								node {
									id
									person {
										id
									}
								}
							}
						}
					}
				}
			}
		}
	`,
});
